import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_BLOG,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_SOPS,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { CONTACT_EMAIL } from "../../constants/contactInfo";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  blogHeroImageSx,
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoPageIntroSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageCompactSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";
import { PublicInfoImageLayout, SopStepRichVariant } from "./enums";
import type { SopStep, UserGuideImageConfig } from "./types";
import { USER_GUIDE_SECTIONS } from "./constants/sopContent";
import {
  USER_GUIDE_HERO_IMAGE,
  USER_GUIDE_SECTION_IMAGES
} from "./constants/userGuideImages";

const UserGuideImage: React.FC<{
  image: UserGuideImageConfig;
  testId: string;
  layout?: PublicInfoImageLayout;
}> = ({ image, testId, layout = PublicInfoImageLayout.SECTION }) => {
  const { t } = useKivunovaTranslation();
  const resolvedLayout =
    layout === PublicInfoImageLayout.SECTION
      ? (image.variant ?? PublicInfoImageLayout.SECTION)
      : layout;
  const imageSx =
    resolvedLayout === PublicInfoImageLayout.HERO
      ? blogHeroImageSx
      : resolvedLayout === PublicInfoImageLayout.COMPACT
        ? userGuideSectionImageCompactSx
        : userGuideSectionImageSx;

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={imageSx}
    />
  );
};

const UserGuideStepBody: React.FC<{ step: SopStep }> = ({ step }) => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  if (step.richVariant === SopStepRichVariant.CONTACT_HELP) {
    return (
      <Typography component="span" variant="body1" sx={publicInfoBodySx}>
        {t(`${step.bodyKey}-prefix`, "Need human help? Use the floating ")}{" "}
        <MuiLink
          component={Link}
          to={contactPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t(`${step.bodyKey}-contact-link`, "Contact us")}
        </MuiLink>
        {CONTACT_EMAIL ? (
          <>
            {t(`${step.bodyKey}-mid`, " button, or ")}{" "}
            <MuiLink
              href={`mailto:${CONTACT_EMAIL}`}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t(`${step.bodyKey}-email-link`, "email support")}
            </MuiLink>
          </>
        ) : (
          t(`${step.bodyKey}-no-email-suffix`, " button.")
        )}
        {CONTACT_EMAIL ? t(`${step.bodyKey}-suffix`, ".") : null}
      </Typography>
    );
  }

  return (
    <Typography component="span" variant="body1" sx={publicInfoBodySx}>
      {t(step.bodyKey, step.bodyFallback)}
    </Typography>
  );
};

const UserGuidePage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const learnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_BLOG, language, defaultLang)
      : `/${PAGE_ROUTE_BLOG}`;
  const runbookPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="user-guide-page"
        eyebrow={t("user-guide-page-eyebrow", "Your setup companion")}
        title={t("user-guide-page-title", "User guide")}
      />

      <Typography
        component="p"
        sx={{ ...publicInfoPageIntroSx, textAlign: "left", mb: 0 }}
        data-testid="user-guide-page-intro"
      >
        {t(
          "user-guide-page-intro-prefix",
          "Step-by-step help from creating your account to managing products, stock, orders, and everyday tasks."
        )}{" "}
        <MuiLink
          component={Link}
          to={runbookPath}
          underline="hover"
          sx={publicInfoLinkSx}
          data-testid="user-guide-ingress-runbook"
        >
          {t("user-guide-page-intro-runbook-link", "Run books")}
        </MuiLink>
        {t("user-guide-page-intro-mid", " provide screenshot walkthroughs.")}{" "}
        <MuiLink
          component={Link}
          to={learnMorePath}
          underline="hover"
          sx={publicInfoLinkSx}
          data-testid="user-guide-ingress-learn-more"
        >
          {t("user-guide-page-intro-learn-more-link", "Learn more")}
        </MuiLink>
        {t(
          "user-guide-page-intro-suffix",
          " explains each area in plain language."
        )}
      </Typography>

      <UserGuideImage
        image={USER_GUIDE_HERO_IMAGE}
        testId="user-guide-hero-image"
        layout={PublicInfoImageLayout.HERO}
      />

      <PublicInfoCallout testId="user-guide-page-note">
        {t(
          "user-guide-page-note",
          "Screens may look slightly different by role and plan. When in doubt, ask your administrator or contact us."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {USER_GUIDE_SECTIONS.map((section, sectionIndex) => {
          const sectionImage = USER_GUIDE_SECTION_IMAGES[section.id];

          return (
            <Box
              key={section.id}
              component="section"
              sx={publicInfoGuideSectionSx}
            >
              <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
                {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
              </Typography>
              {sectionImage ? (
                <UserGuideImage
                  image={sectionImage}
                  testId={`user-guide-section-image-${section.id}`}
                />
              ) : null}
              <Box component="ol" sx={publicInfoOrderedListSx}>
                {section.steps.map((step, i) => (
                  <Box key={`${section.id}-${i}`} component="li">
                    <UserGuideStepBody step={step} />
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Stack>

      <PublicInfoCtaPanel
        title={t("user-guide-page-cta-title", "You're almost ready to run")}
        testId="user-guide-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "user-guide-page-cta-body",
            "Once setup is complete, explore everything Kivunova FMS can do for your team—or reach out if you want a hand."
          )}{" "}
          <MuiLink
            component={Link}
            to={learnMorePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("user-guide-page-cta-learn-more-link", "Learn more")}
          </MuiLink>
          {t("user-guide-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("user-guide-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default UserGuidePage;
